import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  linkedSignal,
  model,
} from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { ControlContainer, FormsModule, NgForm } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatOptionModule } from '@angular/material/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { AdvancedSearchService } from '@lassi/webapp-shared';
import { forkJoin, map } from 'rxjs';

@Component({
  selector: 'lassi-crown-description-search-form',
  imports: [
    FormsModule,
    MatFormFieldModule,
    MatInput,
    MatDialogModule,
    MatSelectModule,
    MatOptionModule,
    MatAutocompleteModule,
  ],
  template: `
    <mat-form-field class="form-field">
      @let parishOptionsLoaded =
        !this.parishTownshipOptionsResource.isLoading() &&
        !this.parishTownshipOptionsResource.error();
      @let parishOptionsError = this.parishTownshipOptionsResource.error();
      <mat-label>
        @if (parishOptionsLoaded) {
          Parish/Township Name
        } @else {
          Loading Parish/Township...
        }
      </mat-label>
      <input
        type="text"
        matInput
        name="parishOrTownshipName"
        [(ngModel)]="parishOrTownshipName"
        [matAutocomplete]="auto"
        maxlength="30"
        #parishOrTownshipNameInput="ngModel"
        required
        (ngModelChange)="updateParishTownshipCodeFromName($event)"
      />
      <mat-autocomplete
        #auto="matAutocomplete"
        (optionSelected)="updateParishTownshipCodeFromName($event.option.value)"
      >
        @if (parishOptionsError) {
          <mat-option [disabled]="true">
            Unable to load Parish/Township names
          </mat-option>
        } @else if (parishOptionsLoaded) {
          @for (option of filteredParishNameOptions(); track option) {
            <mat-option [value]="option.parish_township_name">
              {{ option.parish_township_name }}
            </mat-option>
          }
        } @else {
          <mat-option [disabled]="true">Loading...</mat-option>
        }
      </mat-autocomplete>

      @if (parishOptionsError) {
        <mat-error class="mat-label-medium">
          Unable to load Parish/Township names. Please try again later.
        </mat-error>
      } @else if (parishOrTownshipNameInput.hasError('required')) {
        <mat-error class="mat-label-medium">
          Parish/Township Name is required
        </mat-error>
      }
    </mat-form-field>

    <mat-form-field class="form-field">
      <mat-label>Parish/Township Code</mat-label>
      <input
        matInput
        name="parishOrTownshipCode"
        [(ngModel)]="parishOrTownshipCode"
        #parishOrTownshipCodeInput="ngModel"
        maxlength="5"
        pattern="^[a-zA-Z0-9]{1,5}$"
        required
      />
      <mat-hint>Parish 2001 - 4005 Township 5001 - 5909</mat-hint>
      @if (parishOrTownshipCodeInput.hasError('pattern')) {
        <mat-error class="mat-label-medium">
          Only alphanumeric characters allowed
        </mat-error>
      }
      @if (parishOrTownshipCodeInput.hasError('required')) {
        <mat-error class="mat-label-medium">
          Parish/Township Code is required
        </mat-error>
      }
    </mat-form-field>

    <!-- Allotment Field -->
    <mat-form-field class="long-hint-form-field">
      <mat-label>Allotment</mat-label>
      <input
        matInput
        name="allotment"
        [(ngModel)]="allotment"
        #allotmentInput="ngModel"
        maxlength="10"
        pattern="^[a-zA-Z0-9&+.]{1,10}$"
      />
      <mat-hint> Alphanumeric max 10 chars (eg: 105 or 3A1) </mat-hint>
      @if (allotmentInput.hasError('pattern')) {
        <mat-error class="mat-label-medium">
          Only alphanumeric, &, +, . allowed
        </mat-error>
      }
    </mat-form-field>

    <!-- Section Field -->
    <mat-form-field class="form-field">
      <mat-label>Section</mat-label>
      <input
        matInput
        name="section"
        [(ngModel)]="section"
        #sectionInput="ngModel"
        maxlength="12"
        pattern="^[a-zA-Z0-9]{1,12}$"
      />
      <mat-hint>Alphanumeric max 12 chars (eg: 7 or 188A)</mat-hint>
      @if (sectionInput.hasError('pattern')) {
        <mat-error class="mat-label-medium">
          Only alphanumeric characters allowed
        </mat-error>
      }
    </mat-form-field>

    <!-- Subdivision Field -->
    <mat-form-field class="form-field">
      <mat-label>Subdivision</mat-label>
      <input
        matInput
        name="subdivision"
        [(ngModel)]="subdivision"
        #subdivisionInput="ngModel"
        maxlength="12"
        pattern="^[a-zA-Z0-9]{1,12}$"
      />
      <mat-hint>Alphanumeric max 12 chars (eg: 6 or 47A)</mat-hint>
      @if (subdivisionInput.hasError('pattern')) {
        <mat-error class="mat-label-medium">
          Only alphanumeric characters allowed
        </mat-error>
      }
    </mat-form-field>

    <!-- Portion Field -->
    <mat-form-field class="form-field">
      <mat-label>Portion</mat-label>
      <input
        matInput
        name="portion"
        [(ngModel)]="portion"
        #portionInput="ngModel"
        maxlength="12"
        pattern="^[a-zA-Z0-9]{1,12}$"
      />
      <mat-hint>Alphanumeric max 12 chars (eg: 16A or 120)</mat-hint>
      @if (portionInput.hasError('pattern')) {
        <mat-error class="mat-label-medium">
          Only alphanumeric characters allowed
        </mat-error>
      }
    </mat-form-field>

    <!-- Block Field -->
    <mat-form-field class="form-field">
      <mat-label>Block</mat-label>
      <input
        matInput
        name="block"
        [(ngModel)]="block"
        #blockInput="ngModel"
        maxlength="12"
        pattern="^[a-zA-Z0-9]{1,12}$"
      />
      <mat-hint>Alphanumeric max 12 chars (eg: 56D or 29)</mat-hint>
      @if (blockInput.hasError('pattern')) {
        <mat-error class="mat-label-medium">
          Only alphanumeric characters allowed
        </mat-error>
      }
    </mat-form-field>
  `,
  styles: `
    .form-field {
      display: block;
      margin-top: 0.5rem;
      padding-bottom: 1rem;
    }

    .long-hint-form-field {
      display: block;
      margin-top: 0.5rem;
      padding-bottom: 1.5rem;
    }
  `,
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CrownDescriptionSearchFormComponent {
  private readonly advancedSearchService = inject(AdvancedSearchService);

  parishOrTownshipCode = model<string | null>();
  parishOrTownshipName = linkedSignal(() =>
    this.getParishTownshipNameFromCode(this.parishOrTownshipCode()),
  );
  allotment = model<string | null>();
  section = model<string | null>();
  subdivision = model<string | null>();
  portion = model<string | null>();
  block = model<string | null>();

  readonly parishTownshipOptionsResource = rxResource({
    stream: () =>
      forkJoin({
        parishes: this.advancedSearchService.getParishOptions(),
        townships: this.advancedSearchService.getTownshipOptions(),
      }).pipe(
        map(({ parishes, townships }) => {
          const mappedParishes = parishes.parishes.map((parish) => ({
            pfi: parish.pfi,
            parish_township_name: parish.parish_name,
            parish_township_code: parish.parish_code,
          }));

          const mappedTownships = townships.townships.map((township) => ({
            pfi: township.pfi,
            parish_township_name: township.township_name.endsWith(' TP')
              ? township.township_name.replace(/ TP$/, ' (T)')
              : township.township_name,
            parish_township_code: township.township_code,
          }));

          return [...mappedParishes, ...mappedTownships].sort((a, b) => {
            const nameA = a.parish_township_name;
            const nameB = b.parish_township_name;
            return nameA.localeCompare(nameB);
          });
        }),
      ),
  });

  readonly parishOrTownshipOptions = computed(() =>
    this.parishTownshipOptionsResource.value(),
  );
  readonly filteredParishNameOptions = computed(() => {
    const filterValue = this.parishOrTownshipName()?.toLowerCase() ?? '';
    const options = this.parishOrTownshipOptions() ?? [];
    return options.filter((option) =>
      option.parish_township_name?.toLowerCase().includes(filterValue),
    );
  });

  protected updateParishTownshipCodeFromName(parishTownshipName: string) {
    if (!parishTownshipName) return;
    const selectedParish = this.parishOrTownshipOptions()?.find(
      (option) =>
        option.parish_township_name.toLowerCase() ===
        parishTownshipName.toLowerCase(),
    );

    if (selectedParish?.parish_township_code) {
      this.parishOrTownshipCode.set(selectedParish.parish_township_code);
    } else {
      this.parishOrTownshipCode.set('');
    }
  }

  private getParishTownshipNameFromCode(
    parishTownshipCode?: string | null,
  ): string | null {
    if (!parishTownshipCode) return null;
    const matchingParish = this.parishOrTownshipOptions()?.find(
      (option) =>
        option.parish_township_code.toLowerCase() ===
        parishTownshipCode.toLowerCase(),
    );
    return matchingParish?.parish_township_name ?? null;
  }
}
